import type { CommandDeclaration, CommandField } from "./types";

/** A field's edited value: every type reduces to a string except checkbox. */
export type CommandValue = string | boolean;

export type Compiled = { ok: true; text: string } | { ok: false; error: string };

/**
 * Quote one argument the way pi's `parseCommandArgs` will read it back.
 *
 * That parser has NO escape mechanism -- inside a quote the only terminator is the same quote
 * character, and a backslash is an ordinary character. So a value containing both `"` and `'`
 * genuinely cannot be expressed as a single argument, and the only honest answer is to refuse.
 * Silently stripping a quote would change what the model is told the user said.
 */
export function quoteArg(value: string): Compiled {
	if (value === "") return { ok: true, text: "" };
	if (!/[\s"']/.test(value)) return { ok: true, text: value };
	if (!value.includes('"')) return { ok: true, text: `"${value}"` };
	if (!value.includes("'")) return { ok: true, text: `'${value}'` };
	return { ok: false, error: "不能同时包含单引号和双引号，请改用文件字段" };
}

/**
 * The value each field starts at before the user touches anything.
 *
 * A select must start at its first option, never `""`: an empty select would be dropped by
 * `compileCommand` and every later argument would shift down a slot. `formFields.ts`'s
 * `coerceFieldValue` already applies this rule for model-driven forms; a command dialog needs it
 * more, because here the consequence is a corrupted argument rather than a blank answer.
 */
export function initialCommandValues(fields: CommandField[]): Record<string, CommandValue> {
	const out: Record<string, CommandValue> = {};
	for (const f of fields) {
		out[f.name] = f.type === "checkbox" ? false : f.type === "select" ? (f.options?.[0] ?? "") : "";
	}
	return out;
}

/**
 * Swap in the options the server resolved for `options_from` selects. A value the user already picked
 * survives only if it is still offered; otherwise the select moves to its first option, for the same
 * reason `initialCommandValues` never leaves one blank.
 */
export function applyResolvedOptions(
	fields: CommandField[],
	values: Record<string, CommandValue>,
	resolved: Record<string, string[]>,
): { fields: CommandField[]; values: Record<string, CommandValue> } {
	const next = { ...values };
	const out = fields.map((f) => {
		const options = resolved[f.name];
		if (f.type !== "select" || !options?.length) return f;
		if (!options.includes(String(next[f.name] ?? ""))) next[f.name] = options[0]!;
		return { ...f, options };
	});
	return { fields: out, values: next };
}

/** Required fields the user has left blank. A checkbox is never blank, so it is never missing. */
export function missingRequired(fields: CommandField[], values: Record<string, CommandValue>): CommandField[] {
	return fields.filter((f) => f.required && f.type !== "checkbox" && String(values[f.name] ?? "").trim() === "");
}

/**
 * Build the message text a button sends: exactly what a person typing the same command would
 * produce. See docs/adr/0001-commands-compile-to-message-text.md.
 *
 * Fields map onto `$1..$N` in declaration order. A blank value contributes NO argument, because
 * pi drops empty ones -- `commands.yaml`'s schema guarantees optional fields sit at the tail, so a
 * blank one never shifts a required field. That guarantee is enforced by `commandSchema`'s
 * superRefine in src/profile/commands.ts, and `loadCommands` drops a failing command with a
 * diagnostic rather than serving it. When several optional fields exist, `agent check` requires
 * the workflow to read `$@`, because a blank one does shift the optional fields after it.
 */
export function compileCommand(command: CommandDeclaration, values: Record<string, CommandValue>): Compiled {
	if (!command.workflow) return { ok: false, error: "此按钮仅运行脚本" };
	const parts: string[] = [];
	for (const f of command.fields) {
		const raw = f.type === "checkbox" ? String(values[f.name] === true) : String(values[f.name] ?? "").trim();
		if (raw === "") continue;
		const quoted = quoteArg(raw);
		if (!quoted.ok) return { ok: false, error: `字段「${f.label}」${quoted.error}` };
		parts.push(quoted.text);
	}
	return { ok: true, text: [`/${command.workflow}`, ...parts].join(" ") };
}
